// @ts-nocheck
import { takeEvery, call, put, select } from 'redux-saga/effects'
import { startSubmit, stopSubmit, reset, getFormValues, change } from 'redux-form/immutable'
import { fromJS } from 'immutable'
import { get } from 'lodash'
import moment from 'moment'
import * as Sentry from '@sentry/browser'

import { ADMIN_ORGANIZATION_USERS } from 'store/entities/organizations/actionTypes'
import makeAPIRequest from 'store/entities/api/sagas'
import { parseReduxFormErrorsFromResponse } from 'helpers'
import {
  POST_ADMIN_ORGANIZATION_USERS_PATH,
  POST_SUPER_ADMIN_ORGANIZATION_USERS_PATH,
} from 'app/constants/api/organizations'
import { adminOrganizationUsers } from 'store/entities/organizations/actions'
import { modal } from 'store/entities/modal/actions'
import { getIdentityActiveRoleName } from 'store/entities/identity/selectors'
import * as ROLES from 'app/constants/roles'
import { getOrganizationUsersOrganizationId } from 'store/entities/organizations/selectors'
import { showSuccess, showWarning } from 'store/entities/toastr/utils'
import { ADMIN_ORGANIZATION_USERS_INVITE_FORM as FORM_NAME } from 'app/constants/adminOrganization'
import { parseInvites } from 'helpers/parseInvites'
import { parsePhoneBeforeShow, removePhoneCodeFromPhone } from 'helpers/parsePhone'
import { parseTeamsBeforeAddToSelect } from 'helpers/parseTeams'
import { prepareArray, prepareArrayWithTwoParams } from 'helpers/common'
import { USA_ISO } from 'app/constants/phoneCountryCodes'

export const createTimestampForStepFunction = (isSchedule, schedule) => {
  if (isSchedule && schedule) {
    const hours = schedule.time.slice(0, 2)
    const minutes = schedule.time.slice(3, 5)
    const timezone = schedule.timezone
    const date = schedule.date
    const monthDate = schedule.date.date()
    date.tz(timezone)
    // seconds(moment().format('ss')) doesn't remove because the initialized form has the same seconds for Top and Bottom forms if we don't change the "date" field
    return date.date(monthDate).hours(hours).minutes(minutes).seconds(moment().format('ss')).format()
  }
  return ''
}

export default function* organizationAdminUsersPostSaga() {
  yield takeEvery(ADMIN_ORGANIZATION_USERS.POST.REQUEST, handleOrganizationAdminUsersPost)
}

export function* handleOrganizationAdminUsersPost({
  payload: {
    data: {
      invites,
      schedule,
      isSchedule,
      isInviteOnly,
      isDeleteAndInvite,
      formName,
      autoAccept,
      isCorrectTZatFirstLogin,
      usersTimezone,
      invitedVia,
    },
  },
}: any) {
  let res: any = null
  const FORM_TO_HANDLE = formName || FORM_NAME
  try {
    const role = yield select(getIdentityActiveRoleName)
    const organizationId = yield select(getOrganizationUsersOrganizationId)
    let url = [ROLES.USER, ROLES.ADMIN].includes(role)
      ? POST_ADMIN_ORGANIZATION_USERS_PATH
      : POST_SUPER_ADMIN_ORGANIZATION_USERS_PATH(organizationId)

    yield put(startSubmit(FORM_TO_HANDLE))

    if (isDeleteAndInvite) {
      url = `${POST_SUPER_ADMIN_ORGANIZATION_USERS_PATH(organizationId)}-with-deletion`
    }

    const timestamp = createTimestampForStepFunction(isSchedule, schedule)
    const response = yield call(
      makeAPIRequest,
      url,
      {
        method: 'POST',
        data: {
          users: invites,
          isPeer: role === ROLES.USER,
          timestamp,
          allowTransfer: isInviteOnly || role === ROLES.USER,
          autoAccept,
          isCorrectTZatFirstLogin,
          usersTimezone,
          invitedVia,
        },
      },
      null,
      false,
      true
    )
    res = response.data
    yield put(adminOrganizationUsers.post.success(res))
    const params = {
      length: get(res, 'data.users', []).length,
      date: moment(timestamp).format('dddd, MMMM Do YYYY, h:mm:ss a'),
    }
    const onSuccessMessage = get(res, 'data.message')
    showSuccess({ content: onSuccessMessage, params })
  } catch (badResponse) {
    res = badResponse
    yield put(adminOrganizationUsers.post.fail())
  } finally {
    // close modal with form when make request from dashboard. So should check if values exists
    const formValues = yield select(getFormValues(FORM_TO_HANDLE))
    const isSomeUsersCantBeRemovedByMultiAdmin = get(res, 'data.isSomeUsersCantBeRemovedByMultiAdmin')
    if (isSomeUsersCantBeRemovedByMultiAdmin) {
      showWarning({
        content:
          'You are trying to invite users that are currently active in an organization that you do not own. Please copy the list below and provide it to the super admin.',
      })
    }
    if (res.data.errors && formValues) {
      const errors = parseReduxFormErrorsFromResponse(res)
      const bulkInvites = formValues.get('bulkInvites')
      let users
      if (bulkInvites) {
        users = parseInvites(bulkInvites)
        prepareArrayWithTwoParams(users, parsePhoneBeforeShow, 'phone', 'phoneISO')
        prepareArray(users, parseTeamsBeforeAddToSelect, 'teams')
        users = fromJS(users)
      } else {
        const usersResponse = get(res, ['data', 'errors', 0, 'value'])
        // this needs for correct phone error displaying
        const usersWithRemovedPhoneISO = usersResponse.map(user => {
          if (user.phone) {
            user.phone = removePhoneCodeFromPhone(user.phone, user.phoneISO)
          }
          return user
        })
        prepareArray(usersWithRemovedPhoneISO, parseTeamsBeforeAddToSelect, 'teams')
        users = fromJS(usersWithRemovedPhoneISO)
      }

      try {
        users = users.filter((el: any, index: number) => index in errors.users)
      } catch (e) {
        Sentry.withScope(scope => {
          scope.setExtra('errorInfo', { invites: JSON.stringify(invites) })
          scope.setExtra('where error was happen', 'handleOrganizationAdminUsersPost')
          Sentry.captureException(e)
        })
        const property = get(res, ['data', 'errors', 0, 'property'])
        const value = get(res, ['data', 'errors', 0, 'value'])
        const uniqueViolation = get(res, ['data', 'errors', 0, 'constraints', 'unique violation'])
        errors.users = [{ [property]: uniqueViolation }]
        users = fromJS([{ phoneISO: USA_ISO, [property]: value }])
      }
      errors.users = Object.values(errors.users)
      yield put(change(FORM_TO_HANDLE, 'users', users))
      yield put(change(FORM_TO_HANDLE, 'bulkInvites', ''))
      yield put(stopSubmit(FORM_TO_HANDLE, errors))
      yield put(modal.hide())
    } else {
      yield put(reset(FORM_TO_HANDLE))
    }
  }
}
